import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  refinementStepSchema,
  type RefinementStepData,
  type RequestDetailsStepData,
  type ContactStepData,
  type AIScopingResponse,
} from "@/lib/intake-schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { intakeFullWidthPrimaryCtaClassName } from "@/lib/intake-cta";
import { normalizeAIScopingResponse, isScopingUnavailable } from "@shared/intake-ai-scoping";
import {
  Loader2,
  HelpCircle,
  Lightbulb,
  Video,
  ArrowLeft,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  Clock,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface RefinementStepProps {
  contactData: ContactStepData;
  detailsData: RequestDetailsStepData;
  aiScoping: AIScopingResponse;
  onSubmit: (data: RefinementStepData, refinedAiScoping: AIScopingResponse) => void;
  onBack: () => void;
}

export function RefinementStep({
  contactData,
  detailsData,
  aiScoping,
  onSubmit,
  onBack,
}: RefinementStepProps) {
  const form = useForm<RefinementStepData>({
    resolver: zodResolver(refinementStepSchema),
    defaultValues: {
      missingInfoResponses: {},
      assumptionClarifications: "",
      additionalLoomUrl: "",
    },
  });

  const [refinedAiScoping, setRefinedAiScoping] =
    useState<AIScopingResponse | null>(null);
  const [refineFailed, setRefineFailed] = useState(false);

  const refineEstimateMutation = useMutation({
    mutationFn: async (data: RefinementStepData) => {
      const response = await apiRequest("POST", "/api/intake/ai-scoping", {
        category: detailsData.category,
        requestDetails: detailsData.requestDetails,
        loomUrl: data.additionalLoomUrl || detailsData.loomUrl,
        refinementInput: {
          missingInfo: JSON.stringify(data.missingInfoResponses),
          assumptionContext: data.assumptionClarifications,
        },
        contactData: {
          company: contactData.company,
          email: contactData.email,
        },
      });
      return response;
    },
    onSuccess: async (response) => {
      const refined = normalizeAIScopingResponse(
        await response.json(),
      ) as AIScopingResponse;
      if (isScopingUnavailable(refined)) {
        setRefineFailed(true);
        return;
      }
      setRefineFailed(false);
      setRefinedAiScoping(refined);
    },
    onError: () => {
      setRefineFailed(true);
    },
  });

  const handleFormSubmit = (data: RefinementStepData) => {
    refineEstimateMutation.mutate(data);
  };

  const canContinue =
    (Boolean(refinedAiScoping) || refineFailed) &&
    !refineEstimateMutation.isPending;

  const handleContinue = () => {
    const scoping = refinedAiScoping ?? aiScoping;
    onSubmit(form.getValues(), scoping);
  };

  const missingItems = aiScoping.missing_info || [];
  const assumptions = aiScoping.assumptions || [];

  return (
    <div className="py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className="text-xs font-normal">
            <TrendingUp className="w-3 h-3 mr-1" />
            More detail needed
          </Badge>
        </div>
        <h1 className="text-3xl font-bold mb-2 font-heading">
          A few quick clarifications
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          We found {missingItems.length} thing
          {missingItems.length !== 1 ? "s" : ""} we still need and{" "}
          {assumptions.length} assumption{assumptions.length !== 1 ? "s" : ""}{" "}
          to confirm before we finalise the request summary.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-6"
        >
          {/* Missing Information Section */}
          {missingItems.length > 0 && (
            <Card className="border-border overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-amber-400/60 to-amber-400/10" />
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <HelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold font-heading">
                      We need some details
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Fill in the gaps so we can tighten the estimate:
                    </p>
                  </div>
                </div>

                <div className="space-y-5 ml-0 sm:ml-12">
                  {missingItems.map((item: string, index: number) => (
                    <FormField
                      key={index}
                      control={form.control}
                      name={`missingInfoResponses.${item}`}
                      render={({ field }) => (
                        <FormItem className="animate-in fade-in slide-in-from-left-2 duration-300" style={{ animationDelay: `${index * 80}ms`, animationFillMode: "backwards" }}>
                          <FormLabel className="text-sm font-medium">
                            {item}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Please provide details..."
                              className="min-h-[80px] resize-none"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assumptions Section */}
          {assumptions.length > 0 && (
            <Card className="border-border overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-blue-400/60 to-blue-400/10" />
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Lightbulb className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold font-heading">
                      We're assuming the following
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Let us know if anything here is off:
                    </p>
                  </div>
                </div>

                <ul className="space-y-2 mb-5 ml-0 sm:ml-12">
                  {assumptions.map((assumption: string, index: number) => (
                    <li
                      key={index}
                      className="flex items-start gap-2.5 text-sm animate-in fade-in slide-in-from-left-2 duration-300"
                      style={{ animationDelay: `${index * 60}ms`, animationFillMode: "backwards" }}
                    >
                      <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                      <span>{assumption}</span>
                    </li>
                  ))}
                </ul>

                <div className="ml-0 sm:ml-12">
                  <FormField
                    control={form.control}
                    name="assumptionClarifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Anything we got wrong?
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="e.g., 'We actually need this to work on mobile too' or 'The timeline is much tighter than mentioned'"
                            className="min-h-[80px] resize-none"
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Tell us anything we underestimated or misunderstood
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Optional Loom Section */}
          <Card className="border-dashed border-border bg-accent/20">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                  <Video className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold font-heading">
                    Quick video walkthrough
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    A short Loom video showing the issue or context can help us
                    understand better.
                  </p>
                </div>
              </div>
              <FormField
                control={form.control}
                name="additionalLoomUrl"
                render={({ field }) => (
                  <FormItem className="ml-0 sm:ml-12">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="https://loom.com/share/..."
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              disabled={refineEstimateMutation.isPending}
              className="gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              type="submit"
              disabled={refineEstimateMutation.isPending || !!canContinue}
              className="active-elevate-2 gap-1.5"
            >
              {refineEstimateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Refining...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Refine Estimate
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* Refined estimate result */}
      {refineFailed && !refinedAiScoping && (
        <Alert className="mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            We couldn't update the estimate. You can continue — our team will
            review the request manually.
          </AlertDescription>
        </Alert>
      )}
      {refineFailed && !refinedAiScoping && (
        <Button
          onClick={handleContinue}
          className={`${intakeFullWidthPrimaryCtaClassName} active-elevate-2 mt-4`}
          size="lg"
        >
          Continue without updated estimate
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}
      {refinedAiScoping && (
        <Card className="mt-8 border-emerald-200 dark:border-emerald-800 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="h-1 bg-gradient-to-r from-emerald-500/80 to-emerald-500/20" />
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center gap-2 mb-5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 font-heading">
                Updated Estimate
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 text-center">
                <Clock className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">
                  Estimated Hours
                </p>
                <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                  {refinedAiScoping.estimate_hours_number.toFixed(1)}h
                </p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 text-center">
                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
                <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">
                  Confidence
                </p>
                <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                  {((refinedAiScoping.confidence_score ?? 0.5) * 100).toFixed(0)}
                  %
                </p>
              </div>
            </div>
            <Button
              onClick={handleContinue}
              disabled={!canContinue}
              className={`${intakeFullWidthPrimaryCtaClassName} active-elevate-2`}
              size="lg"
            >
              Continue to Review
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
